import type { OrchestratedOrder } from '@b-stock/fe-core/bstock-next/queries'
import type { AllListingsListing } from '@b-stock/search-api-client'

import type { SearchResult } from '@/queries/listingSearchQuery'

import type { GTMItemInput } from '../types/gtmTypes'

/**
 * The subset of OrchestratedListingDetails that orchestratedListingToGTMItemInput actually reads.
 * Allows us to have a limited type for the adapter without bringing in the entire type.
 */
export type OrchestratedListingGTMInput = {
  _id: string
  prettyId?: string
  auction?: {
    _id?: string
    winningBidAmount?: number | null
    startPrice?: number | null
  } | null
  offering?: { startOfferAmount?: number } | null
  lot: {
    title?: string
    categories?: Array<{ categories?: string[] }>
    itemConditions?: Array<{ itemConditions?: string[] }>
    overrides?: { itemConditions?: Array<{ itemConditions?: string[] }> } | null
  }
  seller: { storefront?: { name?: string } | null }
}

function normalizeCondition(condition: string): string {
  return condition.toLowerCase().replace(/\s+/g, '_')
}

/**
 * For SearchResult (used by useViewItemList, Card.tsx).
 * Has auction3MP/auctionEnt sub-objects with accurate price data.
 */
export function searchResultToGTMItemInput(
  listing: SearchResult
): GTMItemInput {
  const winningBidAmount =
    listing.auction3MP?.winningBidAmount ??
    listing.auctionEnt?.winningBidAmount ??
    (listing.winningBidAmount ? Number(listing.winningBidAmount) : 0)

  const startPrice =
    listing.auction3MP?.startPrice ?? listing.auctionEnt?.startPrice ?? 0

  const price = winningBidAmount || startPrice || 0

  return {
    item_id: listing.listingId || listing.id,
    item_name: listing.title.substring(0, 100) || '',
    price,
    currency: listing.currency || 'USD',
    item_brand: listing.storefrontName || undefined,
    item_category: listing.categories[0] || undefined,
    item_category3: listing.displayedCondition
      ? normalizeCondition(listing.displayedCondition)
      : undefined,
    auctionId:
      listing.auction3MP?._id ??
      listing.auctionEnt?.auctionId ??
      listing.auctionId ??
      undefined,
    listingId: listing.listingId || listing.id,
  }
}

/**
 * For AllListingsListing (used by AuctionCard, ListingLink).
 * Only has the direct winningBidAmount field — no startPrice fallback.
 */
export function allListingsToGTMItemInput(
  listing: AllListingsListing
): GTMItemInput {
  const price = listing.winningBidAmount ? Number(listing.winningBidAmount) : 0

  return {
    item_id: listing.listingId || listing.id,
    item_name: listing.title.substring(0, 100) || '',
    price,
    currency: listing.currency || 'USD',
    item_brand: listing.storefrontName || undefined,
    item_category: listing.categories[0] || undefined,
    item_category3: listing.displayedCondition
      ? normalizeCondition(listing.displayedCondition)
      : undefined,
    auctionId: listing.auctionId ?? undefined,
    listingId: listing.listingId || listing.id,
  }
}

export function orchestratedListingToGTMItemInput(
  listing: OrchestratedListingGTMInput
): GTMItemInput {
  const currentPrice =
    listing.auction?.winningBidAmount ||
    listing.auction?.startPrice ||
    listing.offering?.startOfferAmount ||
    0

  const conditionDtos = listing.lot.overrides?.itemConditions?.length
    ? listing.lot.overrides.itemConditions
    : listing.lot.itemConditions
  const firstCondition = conditionDtos?.[0]?.itemConditions?.[0]
  const condition = firstCondition
    ? normalizeCondition(firstCondition)
    : undefined

  return {
    item_id: listing._id,
    item_name: (listing.lot.title || listing.prettyId || '').substring(0, 100),
    price: currentPrice,
    currency: 'USD',
    item_brand: listing.seller.storefront?.name || undefined,
    item_category: listing.lot.categories?.[0]?.categories?.[0],
    item_category2: listing.lot.categories?.[0]?.categories?.[1],
    item_category3: condition,
    auctionId: listing.auction?._id ?? undefined,
    listingId: listing._id,
  }
}

/**
 * For OrchestratedOrder (used by checkout events).
 * Price comes from the order's totalAmount, not listing-level auction/offering data.
 */
export function orderToGTMItemInput(order: OrchestratedOrder): GTMItemInput {
  const { listing } = order

  const conditionDtos = listing.lot.overrides?.itemConditions?.length
    ? listing.lot.overrides.itemConditions
    : listing.lot.itemConditions
  const firstCondition = conditionDtos?.[0]?.itemConditions?.[0]
  const condition = firstCondition
    ? normalizeCondition(firstCondition)
    : undefined

  return {
    item_id: listing._id,
    item_name: (listing.lot.title || listing.prettyId || '').substring(0, 100),
    price: (order.totalAmount || 0) / 100,
    currency: order.currencyCode || 'USD',
    item_brand: listing.seller.storefront?.name || undefined,
    item_category: listing.lot.categories?.[0]?.categories?.[0],
    item_category2: listing.lot.categories?.[0]?.categories?.[1],
    item_category3: condition,
  }
}
